import React, { useState, useEffect } from "react";
import { Box, Typography, styled, Chip, useMediaQuery, useTheme } from "@mui/material";
import { User, Database, MessageSquare, Clock, CheckCircle, XCircle } from "lucide-react";

interface ProfileData {
  profileName: string;
  collections: string[];
  includeHistory: boolean;
  systemInstruction: string;
  modelId: string;
  numSearchDocuments: number | null;
  numChatMessages: number | null;
  temperature: number | null;
}

interface ProfileSummary {
  name: string;
  collectionsCount: number;
  collections: string[];
  hasHistory: boolean;
  hasCustomInstructions: boolean;
  instructionPreview: string;
}

interface ProfilePreviewProps {
  profileSummary: ProfileSummary;
  profileData: ProfileData;
  isDarkMode: boolean;
  editMode: boolean;
}

const PreviewContainer = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  backgroundColor: $isDark ? "#27272A" : "#F8FAFC",
  border: `1px solid ${$isDark ? "#3F3F46" : "#E2E8F0"}`,
  borderRadius: "12px",
  padding: "24px",
  marginBottom: "16px",
}));

const SectionTitle = styled(Typography)<{ $isDark?: boolean }>(({ $isDark }) => ({
  fontSize: "18px",
  fontWeight: "bold",
  color: $isDark ? "#FFFFFF" : "#1F2937",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const InfoRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  gap: "12px",
});

const InfoLabel = styled(Typography)<{ $isDark?: boolean }>(({ $isDark }) => ({
  fontSize: "14px",
  fontWeight: 600,
  color: $isDark ? "#9CA3AF" : "#6B7280",
  minWidth: "120px",
}));

const InfoValue = styled(Typography)<{ $isDark?: boolean }>(({ $isDark }) => ({
  fontSize: "14px",
  color: $isDark ? "#FFFFFF" : "#374151",
  flex: 1,
}));

const CollectionChip = styled(Chip)<{ $isDark?: boolean }>(({ $isDark }) => ({
  backgroundColor: $isDark ? "#374151" : "#E5E7EB",
  color: $isDark ? "#FFFFFF" : "#374151",
  fontSize: "12px",
  height: "24px",
  margin: "2px",
  "&.default": {
    backgroundColor: "#22C55E",
    color: "#FFFFFF",
  },
}));

const InstructionBox = styled(Box)<{ $isDark?: boolean }>(({ $isDark }) => ({
  backgroundColor: $isDark ? "#18181B" : "#FFFFFF",
  border: `1px solid ${$isDark ? "#374151" : "#D1D5DB"}`,
  borderRadius: "8px",
  padding: "16px",
  marginTop: "8px",
  fontFamily: "monospace",
  fontSize: "13px",
  color: $isDark ? "#D1D5DB" : "#374151",
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  maxHeight: "200px",
  overflow: "auto",
}));

const ProfilePreview = ({
  profileSummary,
  profileData,
  isDarkMode,
  editMode
}: ProfilePreviewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const getPromptPreview = (): string => {
    let preview = "";
    
    if (profileData.includeHistory) {
      preview += `[${textConfig.PROFILE_PREVIEW_CONVERSATION_HISTORY || "Historial de conversación"}]\n\n`;
    }
    
    preview += `[${textConfig.PROFILE_PREVIEW_DOCUMENT_CONTEXT || "Contexto de documentos"}]\n\n`;
    
    if (profileData.systemInstruction.trim()) {
      preview += `${textConfig.PROFILE_PREVIEW_SYSTEM_INSTRUCTIONS || "Instrucciones del Modelo/Asistente"}:\n${profileData.systemInstruction.trim()}\n\n`;
    }
    
    preview += `[${textConfig.PROFILE_PREVIEW_USER_QUESTION || "Pregunta del usuario"}]`;
    
    return preview;
  };

  const getCollectionText = (count: number) => {
    if (count === 1) {
      return `${count} ${textConfig.PROFILE_PREVIEW_COLLECTION_SINGULAR || 'seleccionada'}`;
    } else {
      return `${count} ${textConfig.PROFILE_PREVIEW_COLLECTION_PLURAL || 'seleccionadas'}`;
    }
  };

  return (
    <Box>
      <SectionTitle $isDark={isDarkMode}>
        <User size={20} />
        {textConfig.PROFILE_PREVIEW_TITLE}
      </SectionTitle>
      
      <PreviewContainer $isDark={isDarkMode}>
        <Box mb={3}>
          <Typography
            variant="h6"
            sx={{
              color: "#22C55E",
              fontWeight: "bold",
              mb: 2,
              fontSize: isMobile ? "16px" : "18px"
            }}
          >
            {editMode ? textConfig.PROFILE_PREVIEW_UPDATING : textConfig.PROFILE_PREVIEW_CREATING}: {profileSummary.name}
          </Typography>
          
          <InfoRow>
            <Database size={16} />
            <InfoLabel $isDark={isDarkMode}>{textConfig.PROFILE_PREVIEW_COLLECTIONS_LABEL}:</InfoLabel>
            <InfoValue $isDark={isDarkMode}>
              {getCollectionText(profileSummary.collectionsCount)}
            </InfoValue>
          </InfoRow>
          
          <InfoRow>
            <Clock size={16} />
            <InfoLabel $isDark={isDarkMode}>{textConfig.PROFILE_PREVIEW_HISTORY_LABEL}:</InfoLabel>
            <Box display="flex" alignItems="center" gap={1}>
              {profileSummary.hasHistory ? (
                <CheckCircle size={16} color="#22C55E" />
              ) : (
                <XCircle size={16} color="#EF4444" />
              )}
              <InfoValue $isDark={isDarkMode}>
                {profileSummary.hasHistory ? textConfig.PROFILE_PREVIEW_INCLUDED : textConfig.PROFILE_PREVIEW_NOT_INCLUDED}
              </InfoValue>
            </Box>
          </InfoRow>
          
          <InfoRow>
            <MessageSquare size={16} />
            <InfoLabel $isDark={isDarkMode}>{textConfig.PROFILE_PREVIEW_INSTRUCTIONS_LABEL}:</InfoLabel>
            <Box display="flex" alignItems="center" gap={1}>
              {profileSummary.hasCustomInstructions ? (
                <CheckCircle size={16} color="#22C55E" />
              ) : (
                <XCircle size={16} color="#EF4444" />
              )}
              <InfoValue $isDark={isDarkMode}>
                {profileSummary.hasCustomInstructions ? textConfig.PROFILE_PREVIEW_CUSTOM : textConfig.PROFILE_PREVIEW_DEFAULT}
              </InfoValue>
            </Box>
          </InfoRow>

          <InfoRow>
            <Database size={16} />
            <InfoLabel $isDark={isDarkMode}>Modelo de IA:</InfoLabel>
            <Box display="flex" alignItems="center" gap={1}>
              {profileData.modelId && profileData.modelId.trim() ? (
                <CheckCircle size={16} color="#22C55E" />
              ) : (
                <XCircle size={16} color="#EF4444" />
              )}
              <InfoValue $isDark={isDarkMode}>
                                 {profileData.modelId && profileData.modelId.trim() ? profileData.modelId : "No seleccionado"}
              </InfoValue>
            </Box>
          </InfoRow>

          <InfoRow>
            <Database size={16} />
            <InfoLabel $isDark={isDarkMode}>Temperatura:</InfoLabel>
            <Box display="flex" alignItems="center" gap={1}>
                             {profileData.temperature !== null ? (
                 <CheckCircle size={16} color="#22C55E" />
               ) : (
                 <XCircle size={16} color="#EF4444" />
               )}
              <InfoValue $isDark={isDarkMode}>
                {profileData.temperature !== null ? profileData.temperature : "No configurado"}
              </InfoValue>
            </Box>
          </InfoRow>

          <InfoRow>
            <Database size={16} />
            <InfoLabel $isDark={isDarkMode}>Número de documentos de búsqueda:</InfoLabel>
            <Box display="flex" alignItems="center" gap={1}>
              {profileData.numSearchDocuments !== null ? (
                <CheckCircle size={16} color="#22C55E" />
              ) : (
                <XCircle size={16} color="#EF4444" />
              )}
              <InfoValue $isDark={isDarkMode}>
                {profileData.numSearchDocuments !== null ? profileData.numSearchDocuments : "No configurado"}
              </InfoValue>
            </Box>
          </InfoRow>

          <InfoRow>
            <Database size={16} />
            <InfoLabel $isDark={isDarkMode}>Número de mensajes de chat:</InfoLabel>
            <Box display="flex" alignItems="center" gap={1}>
              {profileData.numChatMessages !== null ? (
                <CheckCircle size={16} color="#22C55E" />
              ) : (
                <XCircle size={16} color="#EF4444" />
              )}
              <InfoValue $isDark={isDarkMode}>
                {profileData.numChatMessages !== null ? profileData.numChatMessages : "No configurado"}
              </InfoValue>
            </Box>
          </InfoRow>
        </Box>

        <Box mb={3}>
          <Typography
            variant="subtitle2"
            sx={{
              color: isDarkMode ? "#FFFFFF" : "#374151",
              fontWeight: 600,
              mb: 1
            }}
          >
            {textConfig.PROFILE_PREVIEW_SELECTED_COLLECTIONS}:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {profileSummary.collections.map((collection) => (
              <CollectionChip
                key={collection}
                label={collection}
                size="small"
                $isDark={isDarkMode}
                className={collection === "DEFAULT_COLLECTION" ? "default" : ""}
              />
            ))}
          </Box>
        </Box>

        {profileSummary.hasCustomInstructions && (
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              sx={{
                color: isDarkMode ? "#FFFFFF" : "#374151",
                fontWeight: 600,
                mb: 1
              }}
            >
              {textConfig.PROFILE_PREVIEW_CUSTOM_INSTRUCTIONS}:
            </Typography>
            <InstructionBox $isDark={isDarkMode}>
              {profileSummary.instructionPreview}
            </InstructionBox>
          </Box>
        )}

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: isDarkMode ? "#FFFFFF" : "#374151",
              fontWeight: 600,
              mb: 1
            }}
          >
            {textConfig.PROFILE_PREVIEW_PROMPT_STRUCTURE}:
          </Typography>
          <InstructionBox $isDark={isDarkMode}>
            {getPromptPreview()}
          </InstructionBox>
        </Box>
      </PreviewContainer>
    </Box>
  );
};

export default ProfilePreview;